import { Button, Descriptions, Divider, Flex, Input, List, Modal, Spin } from "antd"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import isEqual from "lodash/isEqual"
import { DeviceContext, DeviceRequestType, UpdateDeviceContext } from "../../../../context/deviceContext"
import { CompanyContext, UpdateCompanyContext } from "../../../../context/companyContext"
import { PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../../util/helper"
import { useTranslation } from "react-i18next"

const defaultDeviceInfo: DeviceRequestType = {
  company_id: '',
  location: '',
  name: '',
}
export function CompanyDeviceManage() {
  const { t } = useTranslation()
  const { registerDevice, updateDevice, regenerateDeviceKey } = useContext(UpdateDeviceContext)
  const { device } = useContext(DeviceContext)
  const { refreshDevice } = useContext(UpdateDeviceContext)
  const { company } = useContext(CompanyContext)
  const { refreshCompany } = useContext(UpdateCompanyContext)
  const { companyId, deviceId } = useParams()
  const navigate = useNavigate()

  const [deviceInfo, setDeviceInfo] = useState<DeviceRequestType>(defaultDeviceInfo)
  const [originalDeviceInfo, setOriginalDeviceInfo] = useState<DeviceRequestType>(defaultDeviceInfo)
  const [errors, setErros] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isPageReady, setIsPageReady] = useState(false)

  useEffect(() => {
    let isCancelled = false

    const validateAndLoadDeviceManage = async () => {
      setIsPageReady(false)

      if (!companyId || !deviceId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
        return
      }

      const isRegisterMode = deviceId === 'register'

      try {
        const [companyResult, deviceResult] = await Promise.all([
          refreshCompany(companyId),
          isRegisterMode
            ? Promise.resolve({ status: 200, data: null })
            : refreshDevice(deviceId),
        ])

        if (isCancelled) {
          return
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return
        }

        if (!isRegisterMode) {
          if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
            return
          }

          const deviceData = deviceResult.data
          if (!deviceData) {
            navigate(PATH_INFO.ERROR.PATH, { replace: true })
            return
          }

          const deviceCompanyId = deviceData.company?.id
          if (deviceCompanyId !== Number(companyId)) {
            navigate(PATH_INFO.ERROR.PATH, { replace: true })
            return
          }
        }

        if (isRegisterMode) {
          const initialData = { ...defaultDeviceInfo, company_id: companyId }
          setDeviceInfo(initialData)
          setOriginalDeviceInfo(initialData)
        }

        setIsPageReady(true)
      } catch (error) {
        console.error("Error validating company device manage.", error)
        if (isCancelled) {
          return
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true })
      }
    }

    validateAndLoadDeviceManage()

    return () => {
      isCancelled = true
    }
  }, [companyId, deviceId, navigate, refreshCompany, refreshDevice])

  useEffect(() => {
    if (!isPageReady || deviceId === 'register') {
      return
    }

    const initialData = {
      company_id: companyId || '',
      location: device.location,
      name: device.name
    }

    setDeviceInfo(initialData)
    setOriginalDeviceInfo(initialData)
  }, [companyId, device, deviceId, isPageReady])

  const dataSource = useMemo(() => {
    const commonData = [
      {
        key: 'companyName',
        label: t('device.company'),
        value: company?.company_name,
        type: 'text',
        disabled: true
      },
      {
        key: 'location',
        label: t('device.location'),
        value: deviceInfo.location,
        type: 'text',
        onchange: (el: React.ChangeEvent<HTMLInputElement>) => { setDeviceInfo((prev) => ({ ...prev, location: el.target.value })) }
      },
      {
        key: 'name',
        label: t('device.deviceName'),
        value: deviceInfo.name,
        type: 'text',
        onchange: (el: React.ChangeEvent<HTMLInputElement>) => { setDeviceInfo((prev) => ({ ...prev, name: el.target.value })) }
      },
    ]

    if (deviceId !== 'register') {
      commonData.push({
        key: 'api',
        label: t('device.apiKey'),
        value: device.api_key,
        type: 'text',
        disabled: true,
      })
    }

    return commonData
  }, [deviceInfo, device, deviceId, company, t])

  // デバイス登録/編集
  const onClickRegister = useCallback(async () => {
    if (!deviceId) return

    const newErrors: Record<string, boolean> = {};

    ['location', 'name'].forEach((key) => {
      if (deviceInfo[key as keyof DeviceRequestType] === '') {
        newErrors[key] = true
      }
    })

    if (Object.keys(newErrors).length > 0) {
      setErros(newErrors)
      Modal.warning({ content: t('device.requiredLocationAndName'), centered: true })
      return
    }

    setErros({})

    setIsLoading(true)
    try {
      if (deviceId === 'register') {
        const res = await registerDevice(deviceInfo)
        if (res) {
          Modal.success({ content: t('device.registerSucceeded'), centered: true, onOk() { navigate(-1) } })
        } else {
          Modal.error({ content: t('device.registerFailed'), centered: true })
        }
      } else {
        const res = await updateDevice({ location: deviceInfo.location, name: deviceInfo.name, device_id: deviceId })
        if (res) {
          Modal.success({ content: t('device.updateSucceeded'), centered: true, onOk() { navigate(-1) } })
        } else {
          Modal.error({ content: t('device.updateFailed'), centered: true })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }, [deviceInfo, registerDevice, updateDevice, deviceId, navigate, t])

  const onClickBack = useCallback(() => { navigate(-1) }, [navigate])

  const handleRegenerate = useCallback(async () => {
    if (!companyId || !deviceId || deviceId === 'register') return

    const hasUnsavedChanges = !isEqual(originalDeviceInfo, deviceInfo)

    if (!hasUnsavedChanges) {
      Modal.warning({
        title: t('device.regenerateConfirmTitleWithContent'),
        content: t('device.regenerateConfirmQuestion'),
        okType: 'danger',
        okCancel: true,
        centered: true,
        async onOk() {
          setIsLoading(true)
          try {
            const regenerateResult = await regenerateDeviceKey(deviceId)
            if (!regenerateResult) {
              Modal.error({ content: t('device.regenerateFailed'), centered: true })
              return
            }

            const deviceResult = await refreshDevice(deviceId)
            if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
              return
            }

            Modal.success({ content: t('device.regenerateSucceeded'), centered: true, onOk() { navigate(-1) } })
          } catch {
            Modal.error({ content: t('device.regenerateFailed'), centered: true })
          } finally {
            setIsLoading(false)
          }
        }
      })
      return
    }

    Modal.warning({
      title: t('device.unsavedChangesTitle'),
      content: t('device.saveAndRegenerateConfirm'),
      okCancel: true,
      centered: true,
      async onOk() {
        setIsLoading(true)
        try {
          const updateResult = await updateDevice({ location: deviceInfo.location, name: deviceInfo.name, device_id: deviceId })
          if (!updateResult) {
            Modal.error({ content: t('device.updateInfoFailed'), centered: true })
            return
          }

          const regenerateResult = await regenerateDeviceKey(deviceId)
          if (!regenerateResult) {
            Modal.error({ content: t('device.regenerateFailed'), centered: true })
            return
          }

          const deviceResult = await refreshDevice(deviceId)
          if (shouldStopForInvalidContextFetchResult(deviceResult, navigate)) {
            return
          }

          Modal.success({ content: t('device.updateAndRegenerateSucceeded'), centered: true, onOk() { navigate(-1) } })
        } catch (error) {
          Modal.error({ content: t('messages.processFailed'), centered: true })
        } finally {
          setIsLoading(false)
        }
      }
    })

  }, [companyId, deviceId, deviceInfo, originalDeviceInfo, refreshDevice, regenerateDeviceKey, navigate, updateDevice, t])

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t('common.loadingWithDots')} />
      </Flex>
    )
  }

  return (
    <div>
      <Spin spinning={isLoading} fullscreen tip={t('common.processing')} />
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">{deviceId === 'register' ? t('device.registerPage') : t('device.editPage')}</p>
      </div>
      <Divider />
      <Flex vertical align="center">
        <List
          header={<div className="h-4"></div>}
          bordered
          dataSource={dataSource}
          renderItem={(device) => (
            <List.Item style={{ background: '#FFFFFF', justifyContent: 'end' }} >
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={device.label} key={device.key} >
                  {device.key === 'api' ? (
                    <div className="flex space-x-2 w-[320px]">
                      <Input
                        disabled={device.disabled}
                        value={device.value}
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleRegenerate}
                      >
                        {t('device.regenerate')}
                      </Button>
                    </div>
                  ) : (
                    <Input
                      disabled={device.disabled}
                      value={device.value}
                      onChange={device.onchange}
                      status={errors[device.key] ? 'error' : undefined}
                      className="w-[320px]"
                    />
                  )}
                </Descriptions.Item>
              </Descriptions>
            </List.Item>
          )}
          className="overflow-hidden bg-[#FAFAFA] text-center"
        />
        <Flex gap="large" className="mt-5">
          <Button variant="outlined" color="primary" size="large" onClick={onClickBack}>{t('common.back')}</Button>
          <Button type="primary" size="large" onClick={onClickRegister}>{deviceId === 'register' ? t('common.register') : t('common.update')}</Button>
        </Flex>
      </Flex>
    </div>
  )
}
